import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MarketType } from '../types/marketTypes';
import {
  marketTypeLabel,
  marketTypesForSport,
  MARKET_TYPE_LABELS,
  sportLabel,
  SPORT_OPTIONS,
} from '../config/marketControlCatalog';
import {
  MarketControlRequest,
  MarketControlRule,
  marketControlService,
} from '../services/marketControlService';
import { formatDateTime } from '../utils/eventUtils';

// Password for accessing the market controls page (Base64 encoded)
const ENCRYPTED_PASSWORD = 'TUByazN0X0MwbnRyMGwjMjAyNg==';

const decryptPassword = (encrypted: string): string => {
  try {
    return atob(encrypted);
  } catch {
    return '';
  }
};

type PendingAction =
  | { kind: 'apply'; request: MarketControlRequest }
  | { kind: 'clear'; rule: MarketControlRule };

const isClearing = (a: PendingAction): boolean =>
  a.kind === 'clear' || (!a.request.suspendBetting && !a.request.blockCreation);

const scopeOf = (a: PendingAction): { sportId: number | null; marketType: string | null } =>
  a.kind === 'clear'
    ? { sportId: a.rule.sportId, marketType: a.rule.marketType }
    : { sportId: a.request.sportId, marketType: a.request.marketType };

export const MarketControlsPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isError, setIsError] = useState(false);
  const toast = useToast();

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === decryptPassword(ENCRYPTED_PASSWORD)) {
      setIsAuthenticated(true);
      setIsError(false);
    } else {
      setIsError(true);
      toast({ title: 'Access denied', description: 'Incorrect password', status: 'error', duration: 3000, isClosable: true });
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxW="md" py={10}>
        <VStack spacing={6} align="stretch">
          <Heading as="h1" size="xl" textAlign="center">Market Controls</Heading>
          <Text textAlign="center">This page requires a password to manage market controls.</Text>
          <Box as="form" onSubmit={handlePasswordSubmit}>
            <VStack spacing={4}>
              <FormControl isInvalid={isError}>
                <FormLabel>Password</FormLabel>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter password" />
                {isError && <FormErrorMessage>Incorrect password</FormErrorMessage>}
              </FormControl>
              <Button type="submit" colorScheme="blue" width="full">Access Market Controls</Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    );
  }

  return <MarketControls />;
};

const MarketControls: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [sportId, setSportId] = useState<number | null>(null);
  const [marketType, setMarketType] = useState<string>(''); // '' = all market types
  const [suspendBetting, setSuspendBetting] = useState(false);
  const [blockCreation, setBlockCreation] = useState(false);
  const [reason, setReason] = useState('');

  const [rules, setRules] = useState<MarketControlRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  const scopePresent = sportId != null || marketType !== '';
  const typeOptions: MarketType[] = marketTypesForSport(sportId);

  const loadControls = async () => {
    setLoading(true);
    try {
      setRules(await marketControlService.listControls());
    } catch {
      toast({ title: 'Failed to load active controls', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadControls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSportChange = (value: string) => {
    const next = value === '' ? null : Number(value);
    setSportId(next);
    // Drop a market type that the newly selected sport doesn't offer.
    if (marketType && !marketTypesForSport(next).includes(marketType as MarketType)) {
      setMarketType('');
    }
  };

  const openApplyConfirm = () => {
    setPending({
      kind: 'apply',
      request: {
        sportId,
        marketType: marketType === '' ? null : marketType,
        suspendBetting,
        blockCreation,
        reason: reason.trim() || undefined,
      },
    });
    onOpen();
  };

  const openClearConfirm = (rule: MarketControlRule) => {
    setPending({ kind: 'clear', rule });
    onOpen();
  };

  const runPending = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      const result =
        pending.kind === 'clear'
          ? await marketControlService.clearControl(pending.rule)
          : await marketControlService.applyControl(pending.request);
      onClose();
      const cleared = isClearing(pending);
      toast({
        title: cleared ? 'Control cleared' : 'Control applied',
        description: `Re-broadcast ${result.affectedMarkets} market(s) across ${result.affectedEvents} live event(s).`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await loadControls();
    } catch (e: any) {
      const badScope = e?.status === 400;
      toast({
        title: badScope ? 'Invalid control' : 'Failed to apply control',
        description: badScope
          ? 'Pick a sport or a market type, and use a known market type.'
          : 'Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={4} maxW="960px" mx="auto">
      <Heading as="h1" size="xl" mb={1}>Market Controls</Heading>
      <Text color="gray.500" mb={5}>Suspend betting or block creation of markets, scoped by sport and / or market type.</Text>

      <Alert status="info" borderRadius="md" mb={6} alignItems="flex-start">
        <AlertIcon />
        <Text fontSize="sm">
          Controls show the <b>declared</b> state you set here — not the live market status. Turning a suspend off
          resumes markets <b>unless</b> the system is still holding them closed automatically (e.g. a game-phase
          suspend). Blocking creation affects future events only.
        </Text>
      </Alert>

      {/* Apply a control */}
      <Box borderWidth={1} borderRadius="md" boxShadow="sm" p={6} mb={6} bg="white">
        <Heading size="md" mb={1}>Apply a control</Heading>
        <Text color="gray.500" fontSize="sm" mb={5}>
          Choose a scope, set the effects, then apply. Turning both effects off clears the control for that scope.
        </Text>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <FormControl>
            <FormLabel>Sport</FormLabel>
            <Select value={sportId ?? ''} onChange={(e) => onSportChange(e.target.value)}>
              {SPORT_OPTIONS.map(o => (
                <option key={o.label} value={o.id ?? ''}>{o.label}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Market type</FormLabel>
            <Select value={marketType} onChange={(e) => setMarketType(e.target.value)}>
              <option value="">All market types</option>
              {typeOptions.map(t => (
                <option key={t} value={t}>{MARKET_TYPE_LABELS[t]}</option>
              ))}
            </Select>
          </FormControl>
        </SimpleGrid>

        <HStack spacing={10} mt={6} align="flex-start" wrap="wrap">
          <FormControl display="flex" alignItems="center" width="auto">
            <Switch colorScheme="green" isChecked={suspendBetting} onChange={(e) => setSuspendBetting(e.target.checked)} mr={3} />
            <Box>
              <FormLabel mb={0}>Suspend betting</FormLabel>
              <Text fontSize="xs" color="gray.500">Locks live + future markets</Text>
            </Box>
          </FormControl>
          <FormControl display="flex" alignItems="center" width="auto">
            <Switch colorScheme="green" isChecked={blockCreation} onChange={(e) => setBlockCreation(e.target.checked)} mr={3} />
            <Box>
              <FormLabel mb={0}>Block creation</FormLabel>
              <Text fontSize="xs" color="gray.500">Skip on future events</Text>
            </Box>
          </FormControl>
        </HStack>

        <FormControl mt={6}>
          <FormLabel>Reason <Text as="span" color="gray.400" fontWeight="normal">(optional)</Text></FormLabel>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why this control is being applied" rows={2} />
        </FormControl>

        <HStack mt={6} spacing={4}>
          <Button colorScheme="blue" onClick={openApplyConfirm} isDisabled={!scopePresent}>Apply control</Button>
          {!scopePresent && <Text color="gray.500" fontSize="sm">Pick a sport or a market type to enable.</Text>}
        </HStack>
      </Box>

      {/* Active controls */}
      <Box borderWidth={1} borderRadius="md" boxShadow="sm" p={6} bg="white">
        <Heading size="md" mb={1}>Active controls</Heading>
        <Text color="gray.500" fontSize="sm" mb={4}>Currently-declared controls. Clearing sends both effects off for that scope.</Text>

        {loading ? (
          <HStack py={6} justify="center"><Spinner /></HStack>
        ) : rules.length === 0 ? (
          <Text color="gray.500" py={4}>No active controls.</Text>
        ) : (
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Scope</Th>
                  <Th>Suspend</Th>
                  <Th>Block creation</Th>
                  <Th>Reason</Th>
                  <Th>Updated</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {rules.map(rule => (
                  <Tr key={rule.id}>
                    <Td>
                      <ScopeCell sportId={rule.sportId} marketType={rule.marketType} />
                    </Td>
                    <Td><EffectBadge on={rule.suspendBetting} /></Td>
                    <Td><EffectBadge on={rule.blockCreation} /></Td>
                    <Td color="gray.600" maxW="240px" whiteSpace="normal">{rule.reason || '—'}</Td>
                    <Td whiteSpace="nowrap" color="gray.500">{formatDateTime(rule.updatedAt)}</Td>
                    <Td>
                      <Button size="xs" variant="outline" colorScheme="red" onClick={() => openClearConfirm(rule)}>Clear</Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Confirmation */}
      <Modal isOpen={isOpen} onClose={submitting ? () => {} : onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{pending && isClearing(pending) ? 'Clear market control?' : 'Apply market control?'}</ModalHeader>
          <ModalCloseButton isDisabled={submitting} />
          <ModalBody>
            {pending && <ConfirmSummary action={pending} />}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={submitting}>Cancel</Button>
            <Button colorScheme="blue" onClick={runPending} isLoading={submitting}>Confirm</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const EffectBadge: React.FC<{ on: boolean }> = ({ on }) =>
  on ? <Badge colorScheme="green">On</Badge> : <Badge colorScheme="gray">Off</Badge>;

const ScopeCell: React.FC<{ sportId: number | null; marketType: string | null }> = ({ sportId, marketType }) => (
  <Box>
    <Box fontWeight="semibold">
      {sportId == null ? <Badge>All sports</Badge> : sportLabel(sportId)}
    </Box>
    <Box fontSize="xs" color="gray.500" mt={1}>
      {marketType == null ? <Badge>All market types</Badge> : marketTypeLabel(marketType)}
    </Box>
  </Box>
);

const ConfirmSummary: React.FC<{ action: PendingAction }> = ({ action }) => {
  const scope = scopeOf(action);
  const clearing = isClearing(action);
  const suspend = action.kind === 'apply' ? action.request.suspendBetting : false;
  const block = action.kind === 'apply' ? action.request.blockCreation : false;
  const reason = action.kind === 'apply' ? action.request.reason : action.rule.reason;

  return (
    <VStack align="stretch" spacing={3}>
      <Box borderWidth={1} borderRadius="md" p={3}>
        <SummaryRow k="Sport" v={sportLabel(scope.sportId)} />
        <SummaryRow k="Market type" v={marketTypeLabel(scope.marketType)} />
        {!clearing && <SummaryRow k="Suspend betting" v={suspend ? 'On' : 'Off'} />}
        {!clearing && <SummaryRow k="Block creation" v={block ? 'On' : 'Off'} />}
        {reason && <SummaryRow k="Reason" v={reason} />}
      </Box>
      <Text fontSize="sm" color="gray.600">
        {clearing
          ? 'Both effects will be turned off. Markets may stay suspended if the system is still holding them closed automatically.'
          : suspend
            ? 'Live betting on the covered markets will be suspended until you turn it back off.'
            : 'Covered market types will not be created on future events.'}
      </Text>
    </VStack>
  );
};

const SummaryRow: React.FC<{ k: string; v: string }> = ({ k, v }) => (
  <HStack justify="space-between" align="flex-start" py={1}>
    <Text color="gray.500" fontSize="sm">{k}</Text>
    <Text fontWeight="semibold" fontSize="sm" textAlign="right">{v}</Text>
  </HStack>
);
